import { buildApiUrl } from '../config/api';
import type {
  OverviewData,
  NextStepsData,
  PathNextStepsData,
  TopicExamplesData,
  StepExamplesData,
  DropoffDeepDiveData,
} from '../types/journeys';

async function fetchWithAuth<T>(url: string, token: string): Promise<T> {
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Request failed with status ${response.status}`);
  }

  return response.json();
}

export async function fetchOverview(
  projectId: number,
  key: string,
  days: number,
  token: string
): Promise<OverviewData> {
  const url = buildApiUrl(
    `api/v1/projects/${projectId}/journeys_v2/overview?key=${encodeURIComponent(key)}&days=${days}`
  );
  return fetchWithAuth<OverviewData>(url, token);
}

export async function fetchNextSteps(
  projectId: number,
  clusterId: number,
  key: string,
  days: number,
  token: string
): Promise<NextStepsData> {
  const url = buildApiUrl(
    `api/v1/projects/${projectId}/journeys_v2/next_steps?cluster_id=${clusterId}&key=${encodeURIComponent(key)}&days=${days}`
  );
  return fetchWithAuth<NextStepsData>(url, token);
}

// Fetch next steps for a specific PATH of clusters
// This shows what users do AFTER following the exact path (e.g., A → B → C → ?)
// Different from fetchNextSteps which shows what comes after just the last cluster
export async function fetchPathNextSteps(
  projectId: number,
  pathClusterIds: number[],
  key: string,
  days: number,
  token: string
): Promise<PathNextStepsData> {
  const pathParam = pathClusterIds.join(',');
  const url = buildApiUrl(
    `api/v1/projects/${projectId}/journeys_v2/path_next_steps?path=${pathParam}&key=${encodeURIComponent(key)}&days=${days}`
  );
  return fetchWithAuth<PathNextStepsData>(url, token);
}

export async function fetchTopicExamples(
  projectId: number,
  clusterId: number,
  key: string,
  token: string,
  limit: number = 10
): Promise<TopicExamplesData> {
  const url = buildApiUrl(
    `api/v1/projects/${projectId}/journeys_v2/topic_examples?cluster_id=${clusterId}&key=${encodeURIComponent(key)}&limit=${limit}`
  );
  return fetchWithAuth<TopicExamplesData>(url, token);
}

export async function fetchStepExamples(
  projectId: number,
  fromClusterId: number,
  toClusterId: number,
  key: string,
  token: string,
  limit: number = 5
): Promise<StepExamplesData> {
  const url = buildApiUrl(
    `api/v1/projects/${projectId}/journeys_v2/step_examples?from_cluster_id=${fromClusterId}&to_cluster_id=${toClusterId}&key=${encodeURIComponent(key)}&limit=${limit}`
  );
  return fetchWithAuth<StepExamplesData>(url, token);
}

// Fetch detailed drop-off analysis for a specific topic
export async function fetchDropoffDeepDive(
  projectId: number,
  clusterId: number,
  key: string,
  days: number,
  token: string
): Promise<DropoffDeepDiveData> {
  const url = buildApiUrl(
    `api/v1/projects/${projectId}/journeys_v2/dropoff_deep_dive?cluster_id=${clusterId}&key=${encodeURIComponent(key)}&days=${days}`
  );
  return fetchWithAuth<DropoffDeepDiveData>(url, token);
}

// Fetch all clusters for a project (used for topic search/selection)
export interface ClusterListItem {
  id: number;
  label: string;
  key: string;
  size: number;
}

export async function fetchClusters(
  projectId: number,
  token: string
): Promise<ClusterListItem[]> {
  const url = buildApiUrl(`api/v1/projects/${projectId}/clusters?version=latest`);
  const response = await fetchWithAuth<any[]>(url, token);

  return response.map((c) => ({
    id: c.id,
    label: c.label,
    key: c.key,
    size: c.size,
  }));
}
